import type { PortableTextBlock } from '@portabletext/react';
import { captureException } from '@sentry/react';
import { trackAddressAvailabilityResult } from '~/analytics/eventTracker';
import { captureUXEventOnce } from '~/analytics/productAnalytics';
import config from '~/config';
import { useInternetFlowStore, type InternetCustomerAddress, type SingleInternetCustomerAddress } from './useInternetStore';

const displayDataQuery = encodeURIComponent(`{
  "displayData": *[_id=='isp-sales-page'][0] {
    title,
    tvOnlyTitle,
    legalTerms,
    description,
    tvOnlyDescription,
    "image": image.asset->url
  },
  "broadbandProducts": *[_type == "product" && type == "broadband"] {
    description,
    iccAlacarteId,
    iccProductId,
    name,
    productKey,
  }
}`);
export type SanityBroadbandProduct = {
  description: PortableTextBlock;
  iccAlacarteId: number;
  iccProductId: number;
  name: string;
  productKey: 'ngt-fiber-50' | 'ngt-fiber-100';
};
type SanityInternetDataResponse = {
  displayData: {
    title: string;
    description: PortableTextBlock;
    image: string;
    legalTerms: string;
    tvOnlyTitle: string;
    tvOnlyDescription: string;
  };
  broadbandProducts: SanityBroadbandProduct[];
};

export const fetchSanityInternetData = () =>
  fetch(`/api/sanity/${config.sanityDataset}?query=${displayDataQuery}`)
    .then((res) => res.json())
    .then((data) => data.result as SanityInternetDataResponse);

export type SearchResponse = {
  installationAddresses: InternetCustomerAddress[];
};

const cache = new Map<string, Promise<SearchResponse>>();
export const ispAddressLookup = async (searchString: string): Promise<SearchResponse> => {
  captureUXEventOnce('rtv_perform_isp_address_lookup');
  if (!cache.has(searchString)) {
    cache.set(
      searchString,
      fetch(`/api/InternetServiceProviders/address/lookup?address=${searchString}`, {
        method: 'GET',
      })
        .then((r) => r.json())
        .then((json: SearchResponse) => mapResponseToAddresses(json)),
    );
  }
  try {
    return await (cache.get(searchString) as Promise<SearchResponse>);
  } catch (error) {
    captureException(new Error(`Failed to fetch ISP address lookup`, { cause: error }), { extra: { searchString } });
    return { installationAddresses: [] }; // Return empty array on error
  }
};

export const ispAddressRefine = async (addr: InternetCustomerAddress): Promise<SearchResponse> => {
  return await fetch(`/api/InternetServiceProviders/address/refine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addr),
  })
    .then((r) => r.json())
    .then((json: SearchResponse) => mapResponseToAddresses(json));
};

const availabilityCache = new Map<string, Promise<NgtAvailabilityResponse>>();
export const ispServiceAvailabilityCheck = async (addr: SingleInternetCustomerAddress): Promise<NgtAvailabilityResponse> => {
  const cachedValue = availabilityCache.get(addr.description);
  if (cachedValue) {
    return cachedValue;
  }
  const promise = fetch(`/api/InternetServiceProviders/address/availability`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(addr),
  })
    .then((r) => r.json())
    .then((json: NgtAvailabilityResponse) => {
      trackAddressAvailabilityResult({ city: addr.city, postal_code: addr.postalCode, ngt_available: json.serviceAvailable });
      return json;
    })
    .catch((err) => {
      captureException(err, { fingerprint: ['isp-availability-check-failed'], extra: { address: addr.description } });
      useInternetFlowStore.getState().setAvailabilityResponse(undefined);
      return { serviceAvailable: false } as NgtAvailabilityResponse;
    });
  availabilityCache.set(addr.description, promise);
  return promise;
};

const mapResponseToAddresses = (response: SearchResponse): SearchResponse => {
  for (const addr of response.installationAddresses) {
    // Add space after comma
    addr.description = addr.description.replace(/,(\S)/, ', $1');
    // Helper prop for discriminated union
    (addr as any).addrType = addr.count > 1 ? 'multiple' : 'single';
  }
  return response;
};

export type NgtAvailabilityResponse =
  | {
      serviceAvailable: false;
    }
  | NgtAvailabilityResponseYes;
export type NgtAvailabilityResponseYes = {
  serviceAvailable: true;
  availabilityInfo: {
    physicalMedia: 'FIBER' | 'WIRELESS';
    maxDownstreamBandwidth: 1000000;
    maxUpstreamBandwidth: 1000000;
    accessProvider: 'NATIONAL TELENOR';
    lineOwner: 'TELENOR';
    // loopConnectionType: 'BITSTREAM';
    // orderReadiness: 'READY';
    addresses: Array<{ address: { apartment?: string }; installationEffort: string; deliveryStatus: string }>;
  };
  /** list of different internet products like "Fiber 50" and "Fiber 100" */
  availablePackages: Array<NgtProduct>;
};

export type NgtProduct = {
  id: string;
  pickerFolder: 'Fixed Wireless' | 'NGT.no' | 'RiksTV';
  // available_from: '2020-09-13';
  // available_to: '';
  // preference_group_c: 60;
  // cms_key_c: 'BITSTREAM_WIRELESS_TELIA_20MBIT';
  /** we only care about the "Broadband template" in here */
  templates: Array<NgtProductTemplate>;
};
export type NgtProductTemplate = {
  id: string;
  categoryName: 'Broadband' | 'FILTER' | 'Setup-fee';
  productBandwidthUpC: 50000 | 100000 | 20000;
  productBandwidthDownC: 50000 | 100000 | 20000;
  invoiceGroupNoC: 'Internett';
  externalNameNoC: 'Fiber 50' | 'Fiber 100';
  // currencies: {
  //   id: '-99';
  // };
  // availableToC: '';
  // sortOrderC: 2020;
  // billingProcessC: 'subscription';
  // availableFromC: '';
  // externalNameEnC: 'Wireless Internet 20';
  // ossTypeC: 'Internet Standard';
  // invoiceGroupEnC: 'Internet';
  /** Variation for broadband includes monthly or yearly subscription variations of the product */
  variation: Array<{
    id: string;
    // price: `${number}`; // '479.200000';
    // discountAmount: `${number}`; // '0.000000';
    // discountPercentage: null;
    priceVat: `${number}`; // '599.000000';
    // subtotal: `${number}`; // '479.200000';
    // total: `${number}`; // '599.000000';
    commitment: '12M' | '0M';
    // vatRate: 25;
    // startOffset: '0M';
    // endOffset: '12M' | '';
    // availableFrom: '';
    // availableTo: '';
    // priceVariationName: 'RiksTV.no Priser 2025';
  }>;
};
